import React, { useContext, useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { IToastContext, ToastContext } from "../../../providers/Toast";
import { sendConfirmationEmail, signIn, signUp } from "../../../context/auth/action";
import { useFormik } from "formik";
import * as Yup from "yup";
import CustomInputText from "../../../components/shared/forms/input/CustomInputText";
import CustomInputEmail from "../../../components/shared/forms/input/CustomInputEmail";
import CustomInputPassword from "../../../components/shared/forms/input/CustomInputPassword";
import Button from "../../../components/shared/button/Button";
import { useTranslation } from "react-i18next";
import { ROUTES } from "../../../routing/routes";
import PrivacyConsentCheckbox from "../../../components/shared/forms/checkbox/PrivacyConsentCheckbox";
import _environment from "../../../_environment/_environment";

// -----------------------------------------------------------------
// I n t e r f a c e s
// -----------------------------------------------------------------
interface Props {}

const SignUpView = ({ ...restProps }: Props) => {
  // -----------------------------------------------------------------
  // L o c a l   v a r s
  // -----------------------------------------------------------------
  // Access to the history instance that you may use to navigate.
  let history = useHistory();

  // Access to i18n instance that you may use to translate your content.
  const { t } = useTranslation();

  // Access to toast manager hook that you may use to show toasts.
  const { openToast } = useContext<IToastContext>(ToastContext);

  // -----------------------------------------------------------------
  // f o r m
  // -----------------------------------------------------------------
  // Formik Initialisation data
  const form = useFormik({
    initialValues: {
      firstname: "",
      lastname: "",
      email: "",
      password: "",
      privacy_policy_consent: false,
    },
    validationSchema: Yup.object().shape({
      firstname: Yup.string().required("First name is required!"),
      lastname: Yup.string().required("Last name is required!"),
      email: Yup.string().email("Invalid email address").required("Email is required!"),
      password: Yup.string()
        .required("Password is required!")
        .matches(
          /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?([#?!@$%^&*-]|[]]|[[]|[_])).{10,}$/,
          "Must contain at least 10 Characters, one uppercase, one lowercase, one number and one special case character"
        ),
      privacy_policy_consent: Yup.boolean().equals([true]),
    }),
    onSubmit: async (values: any) => {
      setPending(true);
      try {
        await signUp(values);

        openToast({
          type: "success",
          title: "success.general.title",
          message: "success.sign_up_completed",
          callback: () => {},
        });

        history.push({
          pathname: ROUTES.SIGN_IN,
          // search: "?mode=verifyEmail",
        });

        // Redirect to confirm email
        // history.push({
        //   pathname: ROUTES.AUTH_ACTION,
        //   search: "?mode=verifyEmail",
        // });

        // try {
        //   // Send email confirmation
        //   await sendConfirmationEmail(values.email);
        // } catch (err) {
        //   openToast({
        //     type: "warning",
        //     title: "warnings.general.title",
        //     message: "warnings.cannot_send_email",
        //     callback: () => {},
        //   });
        // }
      } catch (err) {
        let message = "errors.general.description";
        if (err.code === "auth/email-already-in-use") {
          message = "errors.mail_already_in_use";
        }
        openToast({
          type: "error",
          title: "errors.general.title",
          message: message,
          callback: () => {},
        });
      } finally {
        setPending(false);
      }
    },
  });

  // -----------------------------------------------------------------
  // N a v i g a t i o n   v a r s
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // S t a t e
  // -----------------------------------------------------------------
  const [pending, setPending] = useState<boolean>(false);

  // -----------------------------------------------------------------
  // R e f s  (DOM)
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // u s e S e l e c t o r   m e t h o d s  (redux)
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // W o r k i n g   m e t h o d s
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // R e n d e r   m e t h o d s
  // -----------------------------------------------------------------
  /**
   * Signup form
   * @function
   */
  const renderForm = (): React.ReactNode => {
    return (
      <form onSubmit={form.handleSubmit} className="user">
        <div className="form-group row">
          <div className="col-sm-6 mb-3 mb-sm-0">
            <CustomInputText
              id="firstname"
              name="firstname"
              label={t("forms.firstname")}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              defaultValue={form.values.firstname}
              errors={form.errors.firstname}
              touched={form.touched.firstname}
            />
          </div>
          <div className="col-sm-6">
            <CustomInputText
              id="lastname"
              name="lastname"
              label={t("forms.lastname")}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              defaultValue={form.values.lastname}
              errors={form.errors.lastname}
              touched={form.touched.lastname}
            />
          </div>
        </div>

        <div className="form-group">
          <CustomInputEmail
            id="email"
            name="email"
            label={t("forms.email")}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            value={form.values.email}
            errors={form.errors.email}
            touched={form.touched.email}
          />
        </div>

        <div className="form-group">
          <CustomInputPassword
            id="password"
            name="password"
            label={t("forms.password")}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            value={form.values.password}
            errors={form.errors.password}
            touched={form.touched.password}
          />
        </div>
        <div className="form-group mt-5">
          <PrivacyConsentCheckbox
            form={form}
            onBlur={form.handleBlur}
            value={form.values.privacy_policy_consent}
            errors={form.errors.privacy_policy_consent}
            touched={form.touched.privacy_policy_consent}
          />
        </div>
        <Button
          loading={pending}
          disabled={pending}
          submit={true}
          className="btn btn-primary btn-block"
        >
          {t("forms.sign_up")}
        </Button>
      </form>
    );
  };

  // -----------------------------------------------------------------
  // L i f e c y c l e
  // -----------------------------------------------------------------
  /**
   * This method is called the first time the component is mounted
   * @function
   */
  const init = (): void => {
    // init component
    console.log("[SignUpView] init");
  };

  /**
   * This method is called when the component is unmounted
   * @function
   */
  const destroy = (): void => {
    // destroy component
    console.log("[SignUpView] destroy");
  };

  // -----------------------------------------------------------------
  // u s e E f f e c t   m e t h o d s
  // -----------------------------------------------------------------
  /**
   * This hook is called once when the component is mounted
   */
  useEffect(() => {
    init();
    return () => {
      destroy();
    };
  }, []);

  // -----------------------------------------------------------------
  // T e m p l a t e
  // -----------------------------------------------------------------
  return (
    <div className="main-content">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-xl-7 col-lg-8 col-md-9">
            <div className="box my-5">
              {/*<div className={"alert alert-warning"}>
                Registrations are temporarily disabled
              </div>*/}
              <div className="logo-vertical-wrapper">
                <a href={_environment.baseURL}>
                  <img src={_environment.assets.logo} alt={t("title")} />
                </a>
              </div>
              <div className="card-body p-0">
                <div className="p-4">
                  <div className="text-center">
                    <h1 className="h4 text-gray-900 mb-4">{t("forms.create_account")}</h1>
                    <p>{t("pages.notRegisteredYet.description")}</p>
                    <br />
                  </div>
                  {renderForm()}

                  <hr />
                  <div className="text-center">
                    <Link className="link-label" to={ROUTES.SIGN_IN}>
                      {t("forms.already_sign_up")}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default SignUpView;
